// sapi-admin/src/services/deliveryTrackingService.js

import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy, Timestamp, addDoc } from 'firebase/firestore';

/**
 * Delivery statuses
 */
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  DELAYED: 'delayed',
  CANCELLED: 'cancelled'
};

/**
 * Urgency levels
 */
export const URGENCY_LEVELS = {
  ON_TIME: { label: '✅ On Time', color: '#34d399', priority: 0 },
  APPROACHING: { label: '⚠️ Approaching', color: '#fbbf24', priority: 1 },
  URGENT: { label: '🔴 Urgent', color: '#f87171', priority: 2 },
  OVERDUE: { label: '🚨 OVERDUE', color: '#ef4444', priority: 3 }
};

/**
 * Calculate urgency level based on remaining days
 */
export const getUrgencyLevel = (estimatedDays, shippedAt) => {
  if (!estimatedDays || !shippedAt) return URGENCY_LEVELS.ON_TIME;
  
  const now = new Date();
  const shipped = new Date(shippedAt);
  const elapsedDays = (now - shipped) / (1000 * 60 * 60 * 24);
  const remainingDays = estimatedDays - elapsedDays;
  
  if (remainingDays > 2) return URGENCY_LEVELS.ON_TIME;
  if (remainingDays > 0) return URGENCY_LEVELS.APPROACHING;
  if (remainingDays > -2) return URGENCY_LEVELS.URGENT;
  return URGENCY_LEVELS.OVERDUE;
};

/**
 * Fetch orders to be delivered with their status
 */
export const getDeliveryOrders = async (filters = {}) => {
  try {
    let q = query(
      collection(db, 'orders'),
      where('status', 'in', ['Placed', 'Shipped', 'Approved', 'Packed']),
      orderBy('orderDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const orders = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const shipping = data.shipping || {};
      
      // ✅ FIX: Correctly convert delivery times to days
      let estimatedDays = 7; // default value
      const deliveryText = shipping.estimatedDelivery || shipping.deliveryDays || shipping.deliveryText || '7 days';
      
      if (typeof deliveryText === 'string') {
        if (deliveryText.includes('24h') || deliveryText === '24h') {
          estimatedDays = 1; // 24h = 1 day
        } else if (deliveryText.includes('2 days') || deliveryText.includes('2-3 days')) {
          estimatedDays = 2;
        } else if (deliveryText.includes('7 days') || deliveryText === '7 days') {
          estimatedDays = 7;
        } else if (deliveryText.includes('10-14 days')) {
          estimatedDays = 12;
        } else {
          // Try to extract a number
          const match = deliveryText.match(/\d+/);
          if (match) {
            estimatedDays = parseInt(match[0]);
          }
        }
      }
      
      const shippedAt = data.shippedAt || data.updatedAt || data.orderDate;
      const urgency = getUrgencyLevel(estimatedDays, shippedAt);
      
      orders.push({
        id: doc.id,
        ...data,
        shipping: {
          ...shipping,
          estimatedDays: shipping.estimatedDays || `${estimatedDays} days`,
          estimatedDaysNumeric: estimatedDays
        },
        urgency,
        shippedAt,
        daysRemaining: urgency.priority > 0 ? 
          Math.ceil((estimatedDays - (new Date() - new Date(shippedAt)) / (1000 * 60 * 60 * 24))) : 
          estimatedDays
      });
    });
    
    // Sort by urgency (most urgent first)
    orders.sort((a, b) => b.urgency.priority - a.urgency.priority);
    
    return orders;
  } catch (error) {
    console.error('Error fetching delivery orders:', error);
    return [];
  }
};

/**
 * Send delivery reminder to admin VIA WHATSAPP
 * Uses fetch (not axios) for sapi-admin
 */
export const sendDeliveryReminder = async (order, adminPhone) => {
  try {
    const message = `📦 *DELIVERY REMINDER - Sapi*\n\n` +
      `Order #${order.orderId || order.id}\n` +
      `Customer: ${order.userName || 'Unknown'}\n` +
      `Phone: ${order.userPhone || 'N/A'}\n` +
      `Status: ${order.status}\n` +
      `Estimated delivery: ${order.shipping?.estimatedDays || '7 days'}\n` +
      `⚠️ ${order.urgency.label}\n\n` +
      `📱 Action required: Please check the status of this order.`;

    // ✅ Using fetch (without axios)
    const response = await fetch('https://sendwhatsappmessage-b3shwrnirq-uc.a.run.app', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: adminPhone,
        message: message
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to send WhatsApp');
    }

    // Save reminder in Firestore
    await addDoc(collection(db, 'deliveryReminders'), {
      orderId: order.id,
      orderNumber: order.orderId || order.id,
      sentAt: Timestamp.now(),
      urgency: order.urgency.label,
      status: 'sent'
    });
    
    return { success: true, data };
  } catch (error) {
    console.error('Error sending delivery reminder:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send delivery reminder to admin VIA PUSH NOTIFICATION (FCM)
 */
export const sendPushReminder = async (order, adminToken) => {
  try {
    const response = await fetch('https://sendnotificationtouser-b3shwrnirq-uc.a.run.app', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: '3Kpi6gIXcNVmmUJsRmhui4qN6af1', // Admin ID
        title: '📦 Delivery Reminder',
        body: `Order #${order.orderId || order.id} - ${order.urgency.label}`,
        data: {
          type: 'delivery_reminder',
          orderId: order.id,
          orderNumber: order.orderId || order.id
        }
      })
    });

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error sending push reminder:', error);
    return { success: false, error: error.message };
  }
};

export default {
  DELIVERY_STATUS,
  URGENCY_LEVELS,
  getUrgencyLevel,
  getDeliveryOrders,
  sendDeliveryReminder,
  sendPushReminder
};